import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import styles from './PackageAvailabilityCard.styles';
import { getFormatMoney, decodeUTF8 } from 'Utils';
import NumberFormat from 'react-number-format';

// TODO: solucionar el tema de Duracion y Fechas

import useBreakpoints from 'Utils/hooks/useBreakpoints';

import LazyImg from 'Components/LazyImg';
import Icon from 'Components/Icon';
import Text from 'Components/Text';
import { Link } from 'react-router-dom';
import { getBasePriceString } from 'Features/packages/helpers';
import Price from 'Components/Price';

const PackageAvailabilityCard = ({
  className,
  currency,
  price,
  title,
  image,
  icons,
  duration,
  lowerLocalPrice,
  travelOn,
  url,
  basePrice,
  rating
}) => {
  const { largeDown, mediumDown } = useBreakpoints();
  const safeIcons = icons || [];
  const [showAllIncludes, setShowAllIncludes] = useState(false);
  const visibleIncludes = showAllIncludes ? safeIcons : safeIcons.slice(0, 4);
  const hiddenIncludes = safeIcons.length - visibleIncludes.length;
  const layout = cs({
    'one-column-container': mediumDown,
    'two-column-container': largeDown && !mediumDown,
    'three-column-container': !largeDown && !mediumDown
  });
  const [basePriceFirst, basePriceSecond] = getBasePriceString(basePrice) || [
    'Precio por persona',
    ''
  ];
  const handleShowAllIncludes = event => {
    event.preventDefault();
    event.stopPropagation();
    setShowAllIncludes(true);
  };

  const handleShowAllIncludesKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleShowAllIncludes(event);
    }
  };

  return (
    <div className={cs(className, layout)}>
      <Link to={url} aria-label={`Ver detalle de ${title}`}>
        <div className="package-availability-card-container">
          <div className="package-availability-card-image">
            {image ? (
              <LazyImg src={image} alt={title} className="img" height={259} />
            ) : (
              <div className="image-placeholder">
                <Text context="white" weight="semi-bold" alignment="center">
                  {title}
                </Text>
              </div>
            )}
            {rating ? (
              <div
                className="tag-holder"
                style={{ backgroundColor: rating.tagColor || undefined }}>
                <Text context="white" weight="semi-bold">
                  {rating && rating.name}
                </Text>
              </div>
            ) : (
              ''
            )}
            {safeIcons.length > 0 && (
              <div className="icons-holder">
                {safeIcons.slice(0, 4).map((icon, index) => (
                  <Icon key={index} name={icon.icon} color="white" size={18} />
                ))}
              </div>
            )}
          </div>

          <div className="content-holder">
            <div className="title-holder">
              <Text paragraph weight="semi-bold" context="secondary" size="large">
                {title}
              </Text>
            </div>

            <div className="meta-holder">
              {duration && (
                <Text size="small" paragraph>
                  {decodeUTF8(duration)}
                </Text>
              )}
              {safeIcons.length > 0 && (
                <div className="include-holder">
                  {visibleIncludes.map((icon, index) => (
                    <span key={index} className="include-chip">
                      {icon.name}
                    </span>
                  ))}
                  {hiddenIncludes > 0 && (
                    <span
                      className="include-chip more"
                      role="button"
                      tabIndex={0}
                      onClick={handleShowAllIncludes}
                      onKeyDown={handleShowAllIncludesKeyDown}>
                      +{hiddenIncludes}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="prices-holder">
              <Text className="price-label" size="small" paragraph>
                Precio desde
              </Text>
              {process.env.REACT_APP_COUNTRY === 'CO' && lowerLocalPrice ? (
                <>
                  <Price
                    isColombia
                    currency={lowerLocalPrice.currency}
                    price={<NumberFormat
                      value={lowerLocalPrice.amount}
                      displayType={'text'}
                      decimalSeparator={','}
                      thousandSeparator={'.'}
                    />}
                  />
                  {lowerLocalPrice && (
                    <Text
                      context="important"
                      alignment="left"
                      paragraph
                      size="small">
                      {getFormatMoney(
                        currency,
                        price
                      )}
                    </Text>
                  )}
                </>
              ) : (
                <>
                  <Price currency={currency} price={price} />
                  {lowerLocalPrice && (
                    <Text
                      context="important"
                      alignment="left"
                      paragraph
                      size="small">
                      {getFormatMoney(
                        lowerLocalPrice.currency,
                        lowerLocalPrice.amount
                      )}
                    </Text>
                  )}
                </>
              )}

              <Text context="important" alignment="left" size="small">
                {basePriceFirst}
              </Text>
              <Text
                weight="semi-bold"
                context="important"
                alignment="left"
                size="small">
                {basePriceSecond}
              </Text>
              {travelOn && (
                <Text className="travel-date" size="small" paragraph>
                  {decodeUTF8(travelOn)}
                </Text>
              )}
            </div>
            <div className="cta-holder">
              <span>Ver detalle</span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

PackageAvailabilityCard.defaultProps = {
  currency: 'USD',
  price: 0,
  title: '',
  image: '',
  icons: [],
  url: '#',
  basePrice: 2,
  color: ''
};

PackageAvailabilityCard.propTypes = {
  className: PropTypes.string,
  currency: PropTypes.string,
  price: PropTypes.number,
  title: PropTypes.string,
  image: PropTypes.string,
  duration: PropTypes.string,
  travelOn: PropTypes.string,
  icons: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired
    })
  ),
  lowerLocalPrice: PropTypes.object,
  color: PropTypes.string,
  index: PropTypes.number,
  url: PropTypes.string,
  basePrice: PropTypes.number,
  rating: PropTypes.shape({
    name: PropTypes.string,
    tagColor: PropTypes.string
  })
};

export default styles(PackageAvailabilityCard);
export { PackageAvailabilityCard };
